
import { useState } from 'react';
import Layout from '../../components/feature/Layout';
import KPICard from './components/KPICard';
import OperationsChart from './components/OperationsChart';
import AlertsPanel from './components/AlertsPanel';
import QuickActions from './components/QuickActions';
import RecentActivity from './components/RecentActivity';
import { useNavigate } from 'react-router-dom';

export default function Dashboard() {
  const navigate = useNavigate();
  const [showNewOperationModal, setShowNewOperationModal] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const handleQuickAction = (action: string) => {
    if (action === 'Nueva Operación') {
      setShowNewOperationModal(true);
    } else if (action === 'Registrar Cliente') {
      navigate('/crm');
    } else if (action === 'Generar Factura') {
      navigate('/erp');
    } else if (action === 'Ver Reportes') {
      navigate('/reports');
    }
  };

  const handleCreateOperation = () => {
    setShowNewOperationModal(false);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
  };

  // Datos de gestiones por estado
  const statusData = [
    { status: 'Documentación', count: 23, color: 'bg-[#EAB308]', percentage: 18 },
    { status: 'En Tránsito', count: 45, color: 'bg-[#2F80ED]', percentage: 35 },
    { status: 'En Aduana', count: 28, color: 'bg-[#114B7A]', percentage: 22 },
    { status: 'Despacho', count: 18, color: 'bg-[#10B981]', percentage: 14 },
    { status: 'Completadas', count: 13, color: 'bg-gray-400', percentage: 11 },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A2A43]">Dashboard Ejecutivo</h1>
            <p className="text-sm text-[#6B7280] mt-1">Visión general de operaciones y métricas clave</p>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={() => navigate('/reports')}
              className="px-4 py-2 border border-gray-200 text-[#6B7280] rounded-md hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-line mr-2"></i>Exportar
            </button>
            <button 
              onClick={() => setShowNewOperationModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-md hover:shadow-md transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>Nueva Operación
            </button>
          </div>
        </div>

        {/* KPI Cards - 6 tarjetas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <KPICard
            title="Operaciones Activas"
            value="127"
            change="+12%"
            trend="up"
            icon="ri-ship-line"
            color="blue"
          />
          <KPICard
            title="En Riesgo"
            value="8"
            change="-3%"
            trend="down"
            icon="ri-alert-line"
            color="red"
          />
          <KPICard
            title="CxC Total"
            value="$2.4M"
            change="+8%"
            trend="up"
            icon="ri-money-dollar-circle-line"
            color="green"
          />
          <KPICard
            title="CxP Total"
            value="$1.1M"
            change="+5%"
            trend="up"
            icon="ri-wallet-line"
            color="yellow"
          />
          <KPICard
            title="Flujo Estimado"
            value="$1.3M"
            change="+15%"
            trend="up"
            icon="ri-funds-line"
            color="blue"
          />
          <KPICard
            title="Clientes Activos"
            value="248"
            change="+7%"
            trend="up"
            icon="ri-user-star-line"
            color="green"
          />
        </div>

        {/* Gestiones por Estado */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-[#0A2A43]">Gestiones por Estado</h3>
              <p className="text-sm text-[#6B7280] mt-1">Distribución actual de operaciones</p>
            </div>
            <button 
              onClick={() => navigate('/operations')}
              className="text-sm text-[#2F80ED] font-medium hover:underline cursor-pointer whitespace-nowrap"
            >
              Ver todas
            </button>
          </div>
          <div className="grid grid-cols-5 gap-4">
            {statusData.map((item, index) => (
              <div 
                key={index} 
                className="text-center p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                onClick={() => navigate('/operations')}
              >
                <div className={`w-12 h-12 ${item.color} rounded-full flex items-center justify-center mx-auto mb-3`}>
                  <span className="text-white font-bold">{item.count}</span>
                </div>
                <p className="text-sm font-semibold text-[#0A2A43]">{item.status}</p>
                <p className="text-xs text-[#6B7280] mt-1">{item.percentage}% del total</p>
              </div>
            ))}
          </div>
          {/* Barra de progreso */}
          <div className="mt-6 h-3 rounded-full overflow-hidden flex">
            {statusData.map((item, index) => (
              <div 
                key={index}
                className={`${item.color} transition-all hover:opacity-80`}
                style={{ width: `${item.percentage}%` }}
              ></div>
            ))}
          </div>
        </div>

        {/* Charts and Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <OperationsChart />
          </div>
          <div>
            <AlertsPanel />
          </div>
        </div>

        {/* Quick Actions and Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div>
            <QuickActions onAction={handleQuickAction} />
          </div>
          <div className="lg:col-span-2">
            <RecentActivity />
          </div>
        </div>
      </div>

      {/* Modal Nueva Operación */}
      {showNewOperationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#0A2A43]">Nueva Operación</h2>
              <button 
                onClick={() => setShowNewOperationModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-[#6B7280]"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Operación</label>
                  <select className="w-full px-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]">
                    <option>Importación</option>
                    <option>Exportación</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cliente</label>
                  <select className="w-full px-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]">
                    <option>Importadora Global S.A.</option>
                    <option>Exportadora del Sur Ltda.</option>
                    <option>Comercial Andina S.A.C.</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Origen</label>
                  <input type="text" placeholder="Ciudad, País" className="w-full px-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Destino</label>
                  <input type="text" placeholder="Ciudad, País" className="w-full px-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Valor Estimado</label>
                  <input type="text" placeholder="$0.00" className="w-full px-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ETA</label>
                  <input type="date" className="w-full px-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción de Mercancía</label>
                <textarea rows={3} placeholder="Descripción detallada..." className="w-full px-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]"></textarea>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button 
                onClick={() => setShowNewOperationModal(false)}
                className="px-4 py-2 border border-gray-200 text-[#6B7280] rounded-md hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button 
                onClick={handleCreateOperation}
                className="px-4 py-2 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-md hover:shadow-md transition-all cursor-pointer whitespace-nowrap"
              >
                Crear Operación
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast de éxito */}
      {showSuccessToast && (
        <div className="fixed bottom-6 right-6 bg-[#10B981] text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 z-50 animate-pulse">
          <i className="ri-check-circle-line text-xl"></i>
          <span className="font-medium">Operación creada exitosamente</span>
        </div>
      )}
    </Layout>
  );
}
